import React, { useState, useEffect, useRef, useMemo } from 'react';
import { toast } from 'react-toastify';
import "react-toastify/dist/ReactToastify.css";
import { Modal, Button, Form } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import DataTable from 'react-data-table-component';
import { createCatalogDeviceCategory, updateCategory, deleteCategory, getAllCategories, getAllCatalogs } from '../commonApi';
import ActionCell from '../Utils/ActionCell';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { Loader } from '../Loader';

// Define the columns outside of the component
const getColumns = (handleEdit, handleDelete) => [
  { 
    name: "Name", 
    selector: (row) => row.name || <span style={{position: 'absolute', left: '58px', top: '18px'}}>—</span>, 
    sortable: true, 
    sortField: "name", 
    width: "300px" 
  },
  { 
    name: "Description", 
    selector: (row) => row.description || <span style={{position: 'absolute', left: '58px', top: '18px'}}>—</span>, 
    sortable: true, 
    sortField: "description", 
    width: "400px" 
  },
  { 
    name: "Catalog Name", 
    selector: (row) => row.catalogName || <span style={{position: 'absolute', left: '58px', top: '18px'}}>—</span>, 
    sortable: true, 
    sortField: "catalogName", 
    width: "460px" 
  },
  {
    name: 'Actions',
    cell: row => (
      <ActionCell
        row={row}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />
    ),
  },
];

const CatalogDeviceCategory = () => {
  const [catalogs, setCatalogs] = useState([]);
  const [form, setForm] = useState({ name: '', description: '', parentCatalogId: '', catalogName: '' });
  const [editing, setEditing] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showCreate, setShowCreate] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [errors, setErrors] = useState({});
  const [dropdownCatalogs, setDropdownCatalogs] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [sortField, setSortField] = useState('id');
  const [sortOrder, setSortOrder] = useState('desc');
  const [isSaveDisabled, setIsSaveDisabled] = useState(false);
  const [isDeleteDisabled, setIsDeleteDisabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const expectedPageRef = useRef(1);

  const nameRef = useRef(null);
  const descriptionRef = useRef(null);
  const parentCatalogRef = useRef(null);
  const catalogNameRef = useRef(null);
  const saveButtonRef = useRef(null);

  useEffect(() => {
    let isCancelled = false;

    const fetchData = async () => {
      setLoading(true);
      try {
        if (!isCancelled) {
          await loadDeviceCategory();
        }
      } catch (error) {
        if (!isCancelled) {
          toast.error('Failed to load catalog device categories');
        }
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    fetchData();
    return () => {
      isCancelled = true;
    };
  }, [page, rowsPerPage, sortField, sortOrder, search]);

  useEffect(() => {
    if (showModal) {
      fetchCatalogs();
    }
  }, [showModal]);

  const loadDeviceCategory = async () => {
    setLoading(true);
    try {
      const response = await getAllCategories(page - 1, rowsPerPage, sortField, sortOrder, search);
      const categories = (response?.data?.data?.content || []).filter(
        (category) => category && typeof category === "object" && category.id
      );
      setCatalogs(categories);
      setTotalRows(response?.data?.data?.totalElements || 0);
    } catch (error) {
      toast.error('Failed to load catalog device categories');
    } finally {
      setLoading(false);
    }
  };

  const fetchCatalogs = async () => {
    try {
      const response = await getAllCatalogs(0, 100, 'id', 'asc', '');
      setDropdownCatalogs(response?.data?.data?.content || []);
    } catch (error) {
      toast.error('Failed to load catalogs');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
    setErrors({ ...errors, [name]: '' });
  };

  const handleDelete = (id) => {
    setDeleteId(id);
    setShowConfirmModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSaveDisabled || (saveButtonRef.current && saveButtonRef.current.disabled)) return;

    const formErrors = validateForm();

    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }

    if (saveButtonRef.current) {
      saveButtonRef.current.disabled = true;
    }
    setIsSaveDisabled(true);

    try {
      if (editing) {
        await updateCategory(editing.id, form);
        toast.success('Catalog device category updated successfully');
      } else {
        await createCatalogDeviceCategory(form.parentCatalogId, {
          name: form.name,
          description: form.description
        });
        toast.success('Catalog device category created successfully');
      }
      resetForm();
      setPage(1);
      expectedPageRef.current = 1;
      await loadDeviceCategory();
      handleCloseModal();
    } catch (error) {
      toast.error(error.response?.data?.error?.details || 'Failed to save catalog device category');
    } finally {
      if (saveButtonRef.current) {
        saveButtonRef.current.disabled = false;
      }
      setIsSaveDisabled(false);
    }
  };

  const validateForm = () => {
    let formErrors = {};

    if (showCreate && !form.parentCatalogId) {
      formErrors.parentCatalogId = 'Please select a Catalog';
      if (parentCatalogRef.current && !formErrors.name && !formErrors.description) parentCatalogRef.current.focus();
    }

    if (!form.name.trim()) {
      formErrors.name = 'Device Category name is required';
      if (nameRef.current) nameRef.current.focus();
    }

    return formErrors;
  };

  const resetForm = () => {
    setForm({ name: '', description: '', parentCatalogId: '', catalogName: '' });
    setEditing(null);
    setErrors({});
  };

  const handleEdit = (id) => {
    const deviceCategory = catalogs.find(c => c.id === id);
    setForm({
      name: deviceCategory.name,
      description: deviceCategory.description,
      parentCatalogId: deviceCategory.parentCatalogId || '',
      catalogName: deviceCategory.catalogName || ''
    });
    setEditing(deviceCategory);
    setShowCreate(false);
    setShowModal(true);
    if (nameRef.current) {
      nameRef.current.focus();
    }
  };

  const confirmDelete = async () => {
    if (isDeleteDisabled) return;

    setIsDeleteDisabled(true);

    try {
      await deleteCategory(deleteId);
      toast.success('Catalog device category deleted successfully');
      setPage(1);
      expectedPageRef.current = 1;
      await loadDeviceCategory();
    } catch (error) {
      toast.error(error.response?.data?.error?.details || 'Failed to delete catalog device category');
    } finally {
      setIsDeleteDisabled(false);
      setShowConfirmModal(false);
      setDeleteId(null);
    }
  };

  const handleSort = (column, sortDirection) => {
    const sortFieldCol = column.sortField || column.selector || 'id';
    setSortField(sortFieldCol);
    setSortOrder(sortDirection);
    setPage(1);
    expectedPageRef.current = 1;
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setForm({ name: '', description: '', parentCatalogId: '', catalogName: '' });
    setEditing(null);
    setErrors({});
  };

  const handleSearchInputChange = (e) => {
    setSearch(e.target.value);
    setPage(1);
    expectedPageRef.current = 1;
  };

  const handlePerRowsChange = (newPerPage) => {
    setRowsPerPage(newPerPage);
    setPage(1);
    expectedPageRef.current = 1;
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    expectedPageRef.current = newPage;
  };

  const columns = useMemo(() => getColumns(handleEdit, handleDelete), [handleEdit, handleDelete]);

  return (
    <div className="custom_tab_wrapper">
      <style>
        {`
          .btn_red {
            background-color: #dc3545; /* Default red */
            border-color: #dc3545;
            color: white;
          }
          .btn_red:disabled {
            background-color: #f28b94; /* Light red for Deleting... */
            border-color: #f28b94;
            color: white;
            opacity: 0.8;
          }
          .btn_light__red {
            background-color: #f8d7da; /* Light red background for Cancel */
            border-color: #f5c6cb;
            color: #721c24;
          }
          .btn_light__red:disabled {
            background-color: #f8d7da; /* Same color when disabled */
            border-color: #f5c6cb;
            color: #721c24;
            opacity: 0.65; /* Bootstrap default opacity for disabled */
          }
        `}
      </style>

      <div className="tab-content" id="myTabContent">
        <div className="tab-pane fade show active" id="home" role="tabpanel" aria-labelledby="home-tab">
          <ul className="nav nav-tabs" id="myTab" >
            <li className="nav-item" >
              <button className="nav-link active" id="home-tab" data-bs-toggle="tab" data-bs-target="#home" type="button" role="tab" aria-controls="home" aria-selected="true">
                DEVICE CATEGORIES: {totalRows}
              </button>
            </li>
          </ul>
          <div className="tab-content" id="myTabContent">
            <div className="tab-pane fade show active" id="home" role="tabpanel" aria-labelledby="home-tab">
              <div className="tab_content_wrapper full_height_tab">
                <div className="custom_table_action">
                  <div>
                    <input
                      className="form-control custom-search-input"
                      type="text"
                      onChange={handleSearchInputChange}
                      placeholder="Search by Name"
                      value={search}
                      style={{ width: '191px' }}
                    />
                  </div>
                  <div>
                    <button
                      style={{ backgroundColor: '#4340d2' }}
                      className="btn btn-primary"
                      type="button"
                      onClick={() => {
                        setForm({ name: '', description: '', parentCatalogId: '', catalogName: '' });
                        setShowCreate(true);
                        setShowModal(true);
                      }}
                      data-bs-toggle="tooltip"
                      data-bs-placement="left"
                      title="Create New Catalog Device Category"
                    >
                      <FontAwesomeIcon icon={faPlus} />
                    </button>
                  </div>
                </div>
                <div className="table-responsive h-100vh-150px">
                  {loading ? (
                    <div className="spin-loader">
                      <Loader />
                    </div>
                  ) : (
                    <div className="custom_table customs-table">
                      <DataTable
                        columns={columns}
                        data={catalogs}
                        pagination
                        paginationServer
                        paginationTotalRows={totalRows}
                        paginationPerPage={rowsPerPage}
                        paginationDefaultPage={page}
                        onSort={handleSort}
                        onChangePage={handlePageChange}
                        onChangeRowsPerPage={handlePerRowsChange}
                        sortServer
                        fixedHeader
                        fixedHeaderScrollHeight="440px"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal for creating/editing a Catalog Device Category */}
      <Modal
        style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh", marginTop: "45px", borderRadius: "5px" }}
        show={showModal}
        onHide={handleCloseModal}
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ color: "#4340d2" }}>{showCreate ? 'Create Catalog Device Category' : 'Edit Catalog Device Category'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            {/* Select Catalog Field */}
            {showCreate && (
              <Form.Group className="mb-3" controlId="formParentCatalog">
                <Form.Label>Select Catalog<span className="required-asterisk" style={{ color: "red" }}>*</span></Form.Label>
                <Form.Control
                  as="select"
                  name="parentCatalogId"
                  value={form.parentCatalogId}
                  onChange={handleChange}
                  ref={parentCatalogRef}
                  isInvalid={!!errors.parentCatalogId}
                >
                  <option value="">Select Catalog</option>
                  {dropdownCatalogs.map(catalog => (
                    <option key={catalog.id} value={catalog.id}>
                      {catalog.catalogName}
                    </option>
                  ))}
                </Form.Control>
                <Form.Control.Feedback type="invalid">
                  {errors.parentCatalogId}
                </Form.Control.Feedback>
              </Form.Group>
            )}
            {/* Catalog Name Field (Hidden in create mode) */}
            {!showCreate && (
              <Form.Group className="mb-3" controlId="formCatalogName">
                <Form.Label>Catalog Name<span className="required-asterisk" style={{ color: "red" }}>*</span></Form.Label>
                <Form.Control
                  type="text"
                  name="catalogName"
                  value={form.catalogName}
                  onChange={handleChange}
                  ref={catalogNameRef}
                  disabled={!showCreate}
                />
              </Form.Group>
            )}

            {/* Device Category Name Field */}
            <Form.Group className="mb-3" controlId="formName">
              <Form.Label>Device Category Name<span className="required-asterisk" style={{ color: "red" }}>*</span></Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={form.name}
                onChange={handleChange}
                ref={nameRef}
                isInvalid={!!errors.name}
              />
              <Form.Control.Feedback type="invalid">
                {errors.name}
              </Form.Control.Feedback>
            </Form.Group>

            {/* Description Field */}
            <Form.Group className="mb-3" controlId="formDescription">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                name="description"
                value={form.description}
                onChange={handleChange}
                ref={descriptionRef}
                isInvalid={!!errors.description}
              />
              <Form.Control.Feedback type="invalid">
                {errors.description}
              </Form.Control.Feedback>
            </Form.Group>

            {/* Modal Footer */}
            <Modal.Footer>
              <button type="button" className="btn light_primary_btn me-3" onClick={handleCloseModal}>
                Cancel
              </button>
              <Button
                className="btn primary_btn"
                type="submit"
                ref={saveButtonRef}
                disabled={isSaveDisabled}
              >
                {isSaveDisabled ? "Saving..." : "Save"}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Confirmation Modal for Deletion */}
      {showConfirmModal && (
        <div
          className="modal fade discard_modal show"
          id="delete-model"
          style={{ display: 'block' }}
          aria-hidden={!showConfirmModal}
          aria-labelledby="exampleModalToggleLabel"
          tabIndex="-1"
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <p className="modal-title" id="staticBackdropLabel">
                  Delete Catalog Device Category
                </p>
              </div>
              <div className="modal-body">
                <p className="modal-msg-title">Confirm Delete</p>
                <p className="modal-msg-desc">Are you sure you want to delete this catalog device category?</p>
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn_light__red mx-2"
                  onClick={() => setShowConfirmModal(false)}
                  disabled={isDeleteDisabled}
                >
                  Cancel
                </button>
                <button
                  className="btn btn_red btn-loader"
                  onClick={confirmDelete}
                  disabled={isDeleteDisabled}
                >
                  {isDeleteDisabled ? "Deleting..." : "Delete"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CatalogDeviceCategory;